/**
 * The favourites list (§10).
 *
 * Shown on the empty state, above the recent list: what someone deliberately
 * kept comes before what merely passed through. Like the recent list, each
 * entry is verified against the store before it is drawn, so a favourite whose
 * file has moved disappears rather than failing on click.
 */
import { useCallback, useEffect, useState } from 'react';
import { FORMATS_BY_ID, formatFileSize } from '@moduviewer/core';
import type { FileStore } from '@moduviewer/core';
import { Icon, iconForFormat } from '@moduviewer/design/icon';
import { pruneMissingFavourites, removeFavourite } from '../lib/favourites.js';
import type { FavouriteFile } from '../lib/favourites.js';

export interface FavouritesProps {
  readonly store: FileStore;
  /** False in a browser, where a handle cannot outlive the tab. */
  readonly reopenable: boolean;
  readonly onOpen: (file: FavouriteFile) => void;
}

export function Favourites({
  store, reopenable, onOpen,
}: FavouritesProps): React.JSX.Element | null {
  const [files, setFiles] = useState<FavouriteFile[] | null>(null);

  const load = useCallback(async () => {
    if (!reopenable) { setFiles([]); return; }
    try {
      setFiles(await pruneMissingFavourites(store));
    } catch {
      // A store that cannot be queried is not a reason to block the app (§34).
      setFiles([]);
    }
  }, [store, reopenable]);

  useEffect(() => { void load(); }, [load]);

  if (files === null || files.length === 0) return null;

  return (
    <section className="favourites" aria-labelledby="favourites-title">
      <div className="favourites__head">
        <h2 className="favourites__title" id="favourites-title">
          <Icon name="star-filled" size={16} className="favourites__star" />
          즐겨찾기
        </h2>
      </div>

      <ul className="favourites__list">
        {files.map((file) => (
          <li key={file.handle} className="recent-item">
            <button
              className="recent-item__open"
              onClick={() => onOpen(file)}
              title={file.handle}
            >
              <Icon
                name={file.formatId
                  ? iconForFormat(file.formatId, FORMATS_BY_ID.get(file.formatId)?.family)
                  : 'file'}
                size={18}
                className="recent-item__icon"
              />
              <span className="recent-item__text">
                <span className="recent-item__name truncate">{file.displayName}</span>
                <span className="recent-item__meta">
                  <span className="tnum">{formatFileSize('ko', file.sizeBytes)}</span>
                </span>
              </span>
            </button>
            <button
              className="btn btn--icon tip"
              data-tip="즐겨찾기에서 제거"
              aria-label={`${file.displayName} 즐겨찾기에서 제거`}
              onClick={() => { removeFavourite(file.handle); void load(); }}
            >
              <Icon name="star-filled" size={16} />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
